import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import {
  DESCRIPTION_LAYOUT,
  getDescriptionDisplay,
  resolveProductDescriptionText,
  stripHtmlToPlainText,
} from "../../../../shared/post-purchase-product-sections.js";
import { resolveSectionContentHeight } from "../../../../shared/text-block-layout.js";
import { isSectionVisibleForViewport } from "../../../../shared/section-visibility.js";
import { useApp } from "../../../context/AppState";
import { resolvePreviewIsMobile } from "../../../helper";
import styles from "./ProductDescriptionBlock.module.css";
import DescriptionChevron from "./DescriptionChevron";

function useDescriptionContentHeight(section, isMobile) {
  return useMemo(() => {
    const viewKey = isMobile ? "mobile" : "desktop";
    return resolveSectionContentHeight(section, viewKey);
  }, [section, isMobile]);
}

function DescriptionSpacer() {
  return <div className={styles.spacer} aria-hidden />;
}

export default function ProductDescriptionBlock({
  section,
  productData,
  variant = "default",
  mode = "full",
  isExpanded = false,
  onToggle,
}) {
  const { isMobile: viewportIsMobile, previewDeviceIsMobile, appState } =
    useApp();
  const isMobile = resolvePreviewIsMobile(
    appState,
    viewportIsMobile,
    previewDeviceIsMobile
  );
  const display = useMemo(() => getDescriptionDisplay(section), [section]);
  const [expanded, setExpanded] = useState(() => display.defaultExpanded);
  const isExtension = variant === "extension";
  const resolvedContentHeight = useDescriptionContentHeight(section, isMobile);
  const contentHeight = isExtension ? null : resolvedContentHeight;

  // Keep local expand state in sync when merchant toggles "Expand by default".
  useEffect(() => {
    setExpanded(Boolean(display.defaultExpanded));
  }, [display.defaultExpanded, section?.id, section?.type]);

  if (!section || !isSectionVisibleForViewport(section, isMobile)) {
    return null;
  }

  const raw = resolveProductDescriptionText(productData, section);
  const plain = stripHtmlToPlainText(raw);
  if (!plain) {
    return mode === "text" || mode === "toggle" ? <DescriptionSpacer /> : null;
  }

  const scrollStyle = contentHeight
    ? {
        maxHeight: contentHeight,
        overflowY: "auto",
        overflowX: "hidden",
      }
    : undefined;

  const handleToggle = (event) => {
    event.stopPropagation();
    if (mode === "toggle" && onToggle) {
      onToggle();
      return;
    }
    setExpanded((open) => !open);
  };

  if (mode === "toggle") {
    return (
      <div className={styles.toggleWrap}>
        <button
          type="button"
          className={styles.toggle}
          aria-expanded={isExpanded}
          onClick={handleToggle}
        >
          <span className={styles.toggleLabel}>{display.collapsibleLabel}</span>
          <DescriptionChevron expanded={isExpanded} />
        </button>
      </div>
    );
  }

  if (mode === "text") {
    return isExpanded ? (
      <p className={styles.expandedText}>{plain}</p>
    ) : (
      <DescriptionSpacer />
    );
  }

  if (display.layout === DESCRIPTION_LAYOUT.EXPANDED) {
    return (
      <div className={`${styles.expanded} ${styles.scrollArea}`} style={scrollStyle}>
        <p className={styles.expandedText}>{plain}</p>
      </div>
    );
  }

  const rootStyle = contentHeight
    ? {
        height: contentHeight,
        maxHeight: contentHeight,
      }
    : undefined;

  return (
    <div className={styles.root} style={rootStyle}>
      <button
        type="button"
        className={styles.toggle}
        aria-expanded={expanded}
        onClick={handleToggle}
      >
        <span className={styles.toggleLabel}>{display.collapsibleLabel}</span>
        <DescriptionChevron expanded={expanded} />
      </button>
      {expanded ? (
        <div className={`${styles.panel} ${styles.scrollArea}`}>
          <p className={styles.panelText}>{plain}</p>
        </div>
      ) : null}
    </div>
  );
}

ProductDescriptionBlock.propTypes = {
  section: PropTypes.object,
  productData: PropTypes.object,
  variant: PropTypes.oneOf(["default", "extension"]),
  mode: PropTypes.oneOf(["full", "toggle", "text"]),
  isExpanded: PropTypes.bool,
  onToggle: PropTypes.func,
};
